/**
 * O ganho que transforma RMS em "cheio".
 *
 * Saiu de `rotas/audio.gravar.tsx`, onde era `estado.nivel * 900` escrito no
 * meio do JSX. Virou constante compartilhada porque as duas telas têm de
 * concordar sobre o que é "alto": um orbe que satura onde a barra ainda está na
 * metade faria a mesma voz parecer duas coisas diferentes em dois lugares.
 *
 * O número vem da medida do sinal, não de gosto: fala normal a 16 kHz mono dá
 * RMS por volta de 0,01 a 0,11, e o ganho põe o topo da escala em RMS ≈ 0,111.
 * A unidade dele é PORCENTO, porque foi assim que nasceu na barra — ver
 * `nivelCheio`, que é onde essa pegadinha está registrada.
 */
export const GANHO_DO_NIVEL = 900;

/**
 * O nível em 0..1, saturando no topo.
 *
 * Divide por 100 porque `GANHO_DO_NIVEL` nasceu numa barra que usa PORCENTO:
 * `min(100, round(nivel * 900))`. Ler aquele 900 como fração fazia o orbe
 * saturar em RMS 0,0011 — praticamente silêncio — e ele ficava cheio o tempo
 * todo, sem responder à voz. Cheio de verdade é RMS ≈ 0,111.
 */
export function nivelCheio(nivel: number): number {
  if (!Number.isFinite(nivel) || nivel <= 0) return 0;
  return Math.min(1, (nivel * GANHO_DO_NIVEL) / 100);
}

/** O mesmo nível em 0..100, para quem desenha barra. */
export function nivelEmPorcento(nivel: number): number {
  return Math.round(nivelCheio(nivel) * 100);
}

/**
 * Quanto do movimento anterior sobrevive a cada quadro.
 *
 * O `ScriptProcessor` entrega bloco de 4096 amostras, ou seja ~85 ms a 48 kHz:
 * o RMS cru pula de 0,02 para 0,09 e volta entre duas sílabas, e um orbe ligado
 * direto nele **treme** em vez de respirar. A média exponencial alisa isso sem
 * atrasar o ataque de forma perceptível.
 */
const PESO_DO_PASSADO = 0.72;

/**
 * Alisa o nível, guardando parte do quadro anterior.
 *
 * Sobe rápido e desce devagar de propósito: começar a falar tem de acender o
 * orbe na hora, e uma pausa curta entre palavras não pode apagá-lo. Assimetria
 * medida no ouvido, não derivada.
 */
export function suavizar(anterior: number, novo: number): number {
  const alvo = nivelCheio(novo);
  // O anterior tambem e preso em 0..1: ele volta desta propria funcao, mas um
  // valor errado vindo de fora nao pode escapar pelo teto.
  const base =
    Number.isFinite(anterior) && anterior > 0 ? Math.min(1, anterior) : 0;
  if (alvo >= base) return alvo;
  return base * PESO_DO_PASSADO + alvo * (1 - PESO_DO_PASSADO);
}

/** O menor e o maior tamanho do orbe, em fração da própria caixa. */
const ESCALA_PARADA = 1;
const ESCALA_CHEIA = 1.35;

/**
 * A escala do orbe para um nível já alisado.
 *
 * Fica entre 1 e 1,35: crescer mais fazia o orbe encostar no texto da
 * transcrição, e a referência tem os dois convivendo com folga.
 */
export function escalaDoOrbe(nivelAlisado: number): number {
  // `Math.max(0, NaN)` e NaN, e NaN numa `transform` some com o orbe inteiro:
  // sem esta guarda um quadro ruim apagaria a tela em vez de nao crescer.
  if (!Number.isFinite(nivelAlisado)) return ESCALA_PARADA;
  const cheio = Math.min(1, Math.max(0, nivelAlisado));
  return ESCALA_PARADA + cheio * (ESCALA_CHEIA - ESCALA_PARADA);
}
